import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, ArrowRight, Shield, Ban, Users, Flag, AlertTriangle, Heart, Scale, Copyright } from 'lucide-react';
import SEO from '../SEO';
import '../SeoPages.css';

function YouTubeGuidelinesGuide() {
  return (
    <>
      <SEO
        title="YouTube Community Guidelines: Complete Guide for Creators (2026)"
        description="Complete guide to YouTube's Community Guidelines for content creators. Learn what's allowed, what's prohibited, and how to keep your channel in good standing."
        keywords="YouTube community guidelines, YouTube rules, YouTube content policy, YouTube terms of service, YouTube creator guidelines, what's allowed on YouTube, YouTube strikes"
        url="/guides/youtube-guidelines"
      />
      <div className="seo-page">
        <div className="seo-container">
          {/* Header */}
          <header className="seo-header">
            <Link to="/" className="seo-back-link">
              <ArrowLeft size={20} />
              <span>Back to Home</span>
            </Link>
            <Link to="/" className="seo-brand">
              <div className="seo-logo-circle">
                <Sparkles className="seo-sparkles-icon" />
              </div>
              <span className="seo-brand-name">MyCleanStudio</span>
            </Link>
          </header>

          {/* Hero */}
          <div className="seo-hero">
            <h1>YouTube Community Guidelines: Complete Guide for Creators</h1>
            <p className="seo-hero-subtitle">
              Everything you need to know about YouTube's rules to keep your channel safe and growing.
            </p>
          </div>

          {/* Content */}
          <main className="seo-content">
            <div className="seo-toc">
              <h3>In This Guide</h3>
              <ul>
                <li><a href="#overview">Community Guidelines Overview</a></li>
                <li><a href="#prohibited">Prohibited Content</a></li>
                <li><a href="#age-restricted">Age-Restricted Content</a></li>
                <li><a href="#advertiser-friendly">Advertiser-Friendly Guidelines</a></li>
                <li><a href="#strikes">Understanding Strikes</a></li>
                <li><a href="#stay-compliant">How to Stay Compliant</a></li>
              </ul>
            </div>

            <section id="overview">
              <h2>Community Guidelines Overview</h2>
              <p>
                YouTube's Community Guidelines are the rules that govern what content is allowed on the platform. They're designed to keep YouTube safe for viewers and creators while allowing creative expression.
              </p>
              <p>
                Violating these guidelines can result in:
              </p>
              <ul>
                <li><strong>Video removal</strong> - Your video gets taken down</li>
                <li><strong>Community Guidelines strike</strong> - A warning that affects your channel</li>
                <li><strong>Channel termination</strong> - Permanent removal (for severe or repeated violations)</li>
                <li><strong>Demonetization</strong> - Loss of ad revenue</li>
              </ul>

              <div className="seo-highlight-box">
                <p>
                  YouTube uses a combination of AI detection and human reviewers to enforce guidelines. Even unintentional violations can result in strikes, which is why it's crucial to understand the rules.
                </p>
              </div>
            </section>

            <section id="prohibited">
              <h2>Prohibited Content</h2>
              <p>
                The following types of content are <strong>never allowed</strong> on YouTube and will result in immediate removal:
              </p>

              <div className="seo-problem-grid">
                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon red">
                    <Ban size={20} />
                  </div>
                  <h4>Hate Speech</h4>
                  <p>Content promoting violence or hatred against individuals or groups based on protected attributes.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon red">
                    <AlertTriangle size={20} />
                  </div>
                  <h4>Violent Content</h4>
                  <p>Graphic violence, inciting violence, or content glorifying violent acts against others.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon red">
                    <Users size={20} />
                  </div>
                  <h4>Harassment & Bullying</h4>
                  <p>Content targeting individuals with prolonged insults, threats, or revealing personal information.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon red">
                    <Heart size={20} />
                  </div>
                  <h4>Child Safety</h4>
                  <p>Any content that endangers minors, including content sexualizing minors.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon yellow">
                    <Flag size={20} />
                  </div>
                  <h4>Harmful Misinformation</h4>
                  <p>Dangerous medical misinformation, election interference, or content promoting harmful conspiracies.</p>
                </div>

                <div className="seo-problem-card">
                  <div className="seo-problem-card-icon purple">
                    <Scale size={20} />
                  </div>
                  <h4>Illegal Activities</h4>
                  <p>Content promoting illegal activities, selling regulated goods, or instructing harmful/illegal acts.</p>
                </div>
              </div>

              <h3>Spam & Deceptive Practices</h3>
              <p>YouTube also prohibits:</p>
              <ul>
                <li>Misleading metadata (clickbait that doesn't match content)</li>
                <li>Scams and fraudulent content</li>
                <li>Artificially inflating views, likes, or subscribers</li>
                <li>Impersonating other channels or individuals</li>
                <li>Spam comments and manipulated engagement</li>
              </ul>
            </section>

            <section id="age-restricted">
              <h2>Age-Restricted Content</h2>
              <p>
                Some content is allowed on YouTube but restricted to viewers 18 and older. Age-restricted videos:
              </p>
              <ul>
                <li>Won't appear in search results for logged-out users</li>
                <li>Can't be watched by users under 18</li>
                <li>Won't be recommended to most viewers</li>
                <li>Cannot be monetized (no ads)</li>
              </ul>

              <h3>What Gets Age-Restricted?</h3>
              <ul>
                <li><strong>Vulgar language</strong> - Heavy profanity throughout the video</li>
                <li><strong>Violence</strong> - Realistic violence, even in educational/documentary context</li>
                <li><strong>Sexually suggestive content</strong> - Not explicit, but suggestive themes</li>
                <li><strong>Dangerous activities</strong> - Stunts that could be imitated</li>
                <li><strong>Drug-related content</strong> - Drug use or detailed discussions</li>
              </ul>

              <div className="seo-warning-box">
                <p>
                  <strong>Note:</strong> Age restriction significantly reduces your video's reach. Even if your audience is 18+, the algorithm won't recommend age-restricted content as widely.
                </p>
              </div>
            </section>

            <section id="advertiser-friendly">
              <h2>Advertiser-Friendly Content Guidelines</h2>
              <p>
                Beyond Community Guidelines, monetized creators must also follow <strong>Advertiser-Friendly Content Guidelines</strong>. These are stricter because advertisers don't want their brands associated with certain content.
              </p>

              <h3>Content That Affects Monetization</h3>
              <ul>
                <li><strong>Strong profanity</strong> - Especially in the first 30 seconds</li>
                <li><strong>Violence</strong> - Even if allowed, may get limited ads</li>
                <li><strong>Adult themes</strong> - Sexual humor, dating content</li>
                <li><strong>Controversial topics</strong> - Politics, sensitive social issues</li>
                <li><strong>Tragedy coverage</strong> - Recent disasters, deaths, conflicts</li>
                <li><strong>Drug references</strong> - Even casual mentions</li>
              </ul>

              <p>
                <Link to="/guides/youtube-demonetization">Learn more about demonetization and the yellow dollar sign &rarr;</Link>
              </p>
            </section>

            <section id="strikes">
              <h2>Understanding Strikes</h2>
              <p>
                When you violate Community Guidelines, you receive a strike. Here's how the system works:
              </p>

              <h3>Strike Progression</h3>
              <ul>
                <li><strong>First Strike:</strong> 1-week restriction on uploading, live streaming, and creating content</li>
                <li><strong>Second Strike (within 90 days):</strong> 2-week restriction on all channel activities</li>
                <li><strong>Third Strike (within 90 days):</strong> Channel permanently terminated</li>
              </ul>

              <h3>Strike Expiration</h3>
              <p>
                Strikes expire after 90 days, but the violation remains on your record. Repeated violations (even after strikes expire) can still result in channel termination.
              </p>

              <div className="seo-highlight-box">
                <p>
                  <strong>Pro Tip:</strong> You can appeal strikes if you believe they were issued in error. You can also complete YouTube's "Copyright School" or "Policy Training" courses to potentially remove a first-time strike early.
                </p>
              </div>
            </section>

            <section id="stay-compliant">
              <h2>How to Stay Compliant</h2>
              <p>
                Following YouTube's guidelines doesn't have to be complicated. Here are best practices:
              </p>

              <div className="seo-feature-list">
                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Shield size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Know the Rules</h4>
                    <p>Regularly review YouTube's Community Guidelines and Advertiser-Friendly Guidelines, as they update frequently.</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Flag size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Self-Certify Accurately</h4>
                    <p>Use YouTube's self-certification honestly. This trains the algorithm and can prevent automated flags.</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <Copyright size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Check for Copyright Issues</h4>
                    <p>Use royalty-free music and always have permission for third-party content. Copyright strikes are separate from Community Guidelines strikes.</p>
                  </div>
                </div>

                <div className="seo-feature-item">
                  <div className="seo-feature-icon">
                    <AlertTriangle size={24} />
                  </div>
                  <div className="seo-feature-content">
                    <h4>Pre-Check Your Content</h4>
                    <p>Before publishing, analyze your content for potential violations. Catching issues before upload saves you from strikes and lost revenue.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA */}
            <div className="seo-cta">
              <h2>Check Your Content Against YouTube's Guidelines</h2>
              <p>Scan your videos for visual issues, copyrighted music, and policy violations before you publish.</p>
              <Link to="/dashboard/new-check" className="seo-cta-button">
                Scan Your Video
                <ArrowRight size={18} />
              </Link>
            </div>
          </main>

          {/* Footer */}
          <footer className="seo-footer">
            <div className="seo-footer-links">
              <Link to="/guides/youtube-demonetization">Demonetization Guide</Link>
              <Link to="/guides/tiktok-guidelines">TikTok Guidelines</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/terms">Terms of Service</Link>
            </div>
            <p>&copy; {new Date().getFullYear()} MyCleanStudio. All rights reserved.</p>
          </footer>
        </div>
      </div>
    </>
  );
}

export default YouTubeGuidelinesGuide;
